import { useEffect, useReducer } from 'react'
import { wf } from '../outline/state.js'
import { ctx } from '../outline/ctx.js'
import { getFirst, getMemberName } from '../outline/utils.js'

function calcItemDetailVis() {
  return !!(wf.zoomItemId && wf.zoomMsId && !wf.searchQuery)
}

export default function ItemDetailPanel() {
  const [, forceUpdate] = useReducer(v => v + 1, 0)

  useEffect(() => {
    function onRender() { forceUpdate() }
    window.addEventListener('outline-render', onRender)
    return () => window.removeEventListener('outline-render', onRender)
  }, [])

  if (!calcItemDetailVis()) return null

  const itemId = wf.zoomItemId
  const msId = wf.zoomMsId
  const items = ctx.clCache?.[msId]

  if (!items) {
    return (
      <div style={{ padding: 24, fontFamily: "'DM Sans',sans-serif", fontSize: 13, color: 'var(--aj-text-muted)' }}>
        Loading...
      </div>
    )
  }

  const record = items.find(i => i.id === itemId)
  if (!record) return null

  const f = record.fields
  const name        = getFirst(f['Name for List (from Deliverable Checklist Item Template)']) || 'Item'
  const summary     = getFirst(f['Summary (from Deliverable Checklist Item Template)']) || ''
  const previewUrl  = getFirst(f['Preview URL (from Learning Unit Module) (from Deliverable Checklist Item Template)']) || ''
  const isDone  = f['Complete'] === true
  const isSkip  = f['Milestone Not Required'] === true

  const compIds  = f['Completed by'] || []
  const compName = compIds.length ? getMemberName(compIds[0]) : ''
  const compDate = (f['Completed Date'] || '').slice(0, 10)

  const nrReason    = f['Milestone Not Required Reason'] || ''
  const nrMemberIds = f['Milestone Not Required Member'] || []
  const nrMember    = nrMemberIds.length ? getMemberName(nrMemberIds[0]) : ''
  const nrTip       = [nrReason, nrMember ? 'by ' + nrMember : ''].filter(Boolean).join(' — ')

  return (
    <div style={{ maxWidth: 640, padding: '8px 0' }}>
      {/* Name */}
      <h2 style={{
        fontFamily: "'DM Sans',sans-serif",
        fontSize: 18,
        fontWeight: 500,
        color: 'var(--aj-text)',
        margin: '0 0 16px',
        lineHeight: 1.4,
      }}>
        {name}
      </h2>

      {/* Status badges */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 20 }}>
        {isDone && (
          <span className="wf-badge wf-badge--green">
            {compName ? 'Done by ' + compName : 'Complete'}
            {compDate ? ' · ' + compDate : ''}
          </span>
        )}
        {isSkip && (
          <span className="wf-badge wf-badge--amber" title={nrTip} style={{ cursor: 'help' }}>
            Not required{nrMember ? ' · ' + nrMember : ''}
          </span>
        )}
      </div>

      {/* Summary */}
      {summary && (
        <div style={{
          fontFamily: "'DM Sans',sans-serif",
          fontSize: 14,
          color: 'var(--aj-text-mid)',
          lineHeight: 1.6,
          marginBottom: 20,
          padding: '14px 16px',
          background: 'var(--aj-bg-subtle)',
          borderRadius: 6,
          borderLeft: '3px solid var(--aj-border-mid)',
        }}>
          {summary}
        </div>
      )}

      {/* Preview link */}
      {previewUrl && (
        <a
          href={previewUrl}
          target="_blank"
          rel="noopener"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            fontFamily: "'DM Sans',sans-serif",
            fontSize: 13,
            color: '#1a6fa8',
            textDecoration: 'none',
            marginBottom: 20,
          }}
        >
          <svg width="13" height="13" viewBox="0 0 16 16" fill="none">
            <path d="M6 2H3a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1v-3"
              stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
            <path d="M9 2h5v5M14 2L8 8"
              stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          View learning resource
        </a>
      )}

      {/* Action buttons */}
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 8,
        paddingTop: 4,
        borderTop: '0.5px solid var(--aj-border)',
        marginTop: 4,
      }}>
        {isSkip ? (
          <button className="wf-item-detail-btn"
            data-fn="markRequired" data-id={itemId} data-msid={msId}>
            Mark required
          </button>
        ) : isDone ? (
          <button className="wf-item-detail-btn"
            data-fn="completeItem" data-id={itemId} data-msid={msId}>
            Mark incomplete
          </button>
        ) : (
          <>
            <button className="wf-item-detail-btn wf-item-detail-btn--primary"
              data-fn="completeItem" data-id={itemId} data-msid={msId}>
              Mark complete
            </button>
            {isDel ? (
              <button className="wf-item-detail-btn"
                data-fn="removeSupport" data-itemid={itemId} data-msid={msId}>
                Remove support
              </button>
            ) : (
              <button className="wf-item-detail-btn"
                data-fn="openSupportModal" data-itemid={itemId} data-msid={msId}>
                Assign support
              </button>
            )}
            <button className="wf-item-detail-btn"
              data-fn="inlineNotRequired" data-itemid={itemId} data-msid={msId}>
              Not required
            </button>
          </>
        )}
      </div>
    </div>
  )
}
